import { DisclosureChevron, useDisclosure } from "@/components/ui/Disclosure";
import { StatusPill } from "@/components/ui/StatusPill";
import { Panel } from "@/components/ui/Panel";
import { ChatMarkdown } from "@/components/chat/ChatMarkdown";
import { changelog, type Release } from "@/lib/changelog";
import { BackToClients, ClientsPageFrame } from "@/v2/clients/ClientsLayout";

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** The raw GitHub notes, behind the plain-English summary that stands for them. */
function TechnicalDetails({ body }: { body: string }) {
  const { open, trigger, panel } = useDisclosure();
  return (
    <div>
      <button
        {...trigger}
        className="-mx-2 flex w-full cursor-pointer items-center justify-between rounded-lg px-2 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50"
      >
        Technical details
        <DisclosureChevron open={open} />
      </button>
      <div {...panel} className="pb-4 text-sm">
        {open && <ChatMarkdown content={body} />}
      </div>
    </div>
  );
}

/** One release in the list: the newest stands open, the rest fold away. */
function ReleaseRow({
  release,
  openByDefault,
}: {
  release: Release;
  openByDefault: boolean;
}) {
  const { open, trigger, panel } = useDisclosure(openByDefault);
  return (
    <div className="border-b">
      <button
        {...trigger}
        className="-mx-2 flex w-full cursor-pointer items-center justify-between rounded-lg px-2 py-2 text-sm font-medium transition-colors hover:bg-muted/50"
      >
        <span className="flex items-center gap-3">
          <span className="font-semibold">{release.name}</span>
          {formatDate(release.publishedAt) && (
            <span className="text-xs font-normal text-muted-foreground">
              {formatDate(release.publishedAt)}
            </span>
          )}
        </span>
        <DisclosureChevron open={open} className="text-muted-foreground" />
      </button>
      <div {...panel} className="pb-4 pt-0">
        {open && <ReleaseNotes release={release} />}
      </div>
    </div>
  );
}

/**
 * Renders one release's notes: the plain-English summary by default, with the
 * raw GitHub notes tucked behind a "Technical details" toggle. Falls back to the
 * raw notes when no summary was generated (offline builds, AI failure).
 */
function ReleaseNotes({ release }: { release: Release }) {
  if (release.friendlyBody) {
    return (
      <div className="space-y-3">
        <ChatMarkdown content={release.friendlyBody} />
        <TechnicalDetails body={release.body} />
      </div>
    );
  }

  if (release.body) {
    return <ChatMarkdown content={release.body} />;
  }

  return (
    <p className="text-sm text-muted-foreground">No notes for this release.</p>
  );
}

interface ChangelogViewProps {
  /** Build-accurate version, e.g. "v0.3.0" on prod or "v0.3.0-5-ga1b2c3d" on staging. */
  version: string;
  releases: Release[];
}

/** Pure presentation — takes data as props so it can be tested without the build-time JSON. */
export function ChangelogView({ version, releases }: ChangelogViewProps) {
  return (
    <div className="mx-auto max-w-3xl p-6">
      <Panel pad="none">
        <div className="flex flex-col space-y-1.5 p-4 pb-0">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-semibold leading-none tracking-tight">
              What&apos;s new
            </div>
            {version && <StatusPill tone="brand">{version}</StatusPill>}
          </div>
          <p className="text-sm text-muted-foreground">
            Release notes for Patent Studio, published from GitHub.
          </p>
        </div>
        <div className="p-4">
          {releases.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No release notes are available yet.
            </p>
          ) : (
            <div>
              {releases.map((release, at) => (
                <ReleaseRow
                  key={release.tag}
                  release={release}
                  openByDefault={at === 0}
                />
              ))}
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}

/**
 * The notes under the client pages' header. The header carries no trail here,
 * since the notes sit beside the clients rather than under one, so the way
 * back is a link above them.
 */
export function ChangelogPage() {
  return (
    <ClientsPageFrame crumbs={[]}>
      <div className="mx-auto max-w-3xl px-6 pt-6">
        <BackToClients />
      </div>
      <ChangelogView
        version={changelog.build || changelog.version}
        releases={changelog.releases}
      />
    </ClientsPageFrame>
  );
}
